import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest } from '../utils/api';
import './CharacterEquipment.css';

interface EquipmentItem {
  id: number;
  name: string;
  itemType: string;
  equipmentSlot?: string;
  isTwoHanded: boolean;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  mpBonus: number;
  speedBonus: number;
  durability: number;
  maxDurability: number;
  // Weapon properties
  minDamage?: number;
  maxDamage?: number;
  minRange?: number;
  maxRange?: number;
  damageType?: string;
  aoeShape?: string;
  weaponAPCost?: number;
  usesAmmo?: boolean;
  currentAmmo?: number;
  maxAmmo?: number;
  imageUrl?: string;
  iconName?: string;
}

interface EquipmentSlots {
  mainHand: EquipmentItem | null;
  offHand: EquipmentItem | null;
  head: EquipmentItem | null;
  chest: EquipmentItem | null;
  legs: EquipmentItem | null;
  feet: EquipmentItem | null;
  hands: EquipmentItem | null;
  necklace: EquipmentItem | null;
  ring1: EquipmentItem | null;
  ring2: EquipmentItem | null;
  belt: EquipmentItem | null;
  back: EquipmentItem | null;
}

interface InventoryItem {
  id: number;
  itemName: string;
  itemType: number;
  equipmentSlot?: number;
  isTwoHanded: boolean;
  requiredLevel: number;
  requiredClass?: string;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  mpBonus: number;
  speedBonus: number;
  durability: number;
  maxDurability: number;
  isEquipped: boolean;
  // Weapon properties
  minDamage?: number;
  maxDamage?: number;
  minRange?: number;
  maxRange?: number;
  damageType?: string;
  aoeShape?: string;
  weaponAPCost?: number;
  usesAmmo?: boolean;
  currentAmmo?: number;
  maxAmmo?: number;
}

interface CharacterEquipmentProps {
  characterId: number;
  onBack: () => void;
}

const CharacterEquipment: React.FC<CharacterEquipmentProps> = ({ characterId, onBack }) => {
  const { handleAuthError } = useAuth();
  const { checkAndStartTutorial } = useTutorial();

  const [character, setCharacter] = useState<any>(null);
  const [equipment, setEquipment] = useState<EquipmentSlots | null>(null);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [consumables, setConsumables] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [draggedItem, setDraggedItem] = useState<InventoryItem | null>(null);
  const [slotFilter, setSlotFilter] = useState<string>('all');

  // Helper function to convert equipment slot enum to readable name
  const getSlotName = (slotEnum: number | undefined): string => {
    if (slotEnum === undefined) return 'Unknown';
    const slotNames: { [key: number]: string } = {
      0: 'MainHand',
      1: 'OffHand',
      2: 'Head',
      3: 'Chest',
      4: 'Legs',
      5: 'Feet',
      6: 'Hands',
      7: 'Necklace',
      8: 'Ring1',
      9: 'Ring2',
      10: 'Belt',
      11: 'Back',
    };
    return slotNames[slotEnum] || 'Unknown';
  };

  useEffect(() => {
    const loadData = async () => {
      const char = await loadCharacterData();
      await loadEquipment();
      await loadInventory(char);
    };
    loadData();
  }, [characterId]);

  // Auto-trigger equipment tutorial for first-time players
  useEffect(() => {
    if (equipment && !isLoading) {
      checkAndStartTutorial('equipment-basics');
    }
  }, [equipment, isLoading, checkAndStartTutorial]);

  const loadCharacterData = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/characters/${characterId}`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        setCharacter(data);
        return data;
      }
    } catch (err) {
      console.error('Error loading character:', err);
      if (err instanceof Error && err.message.includes('401')) {
        handleAuthError();
      }
    }
    return null;
  };

  const loadEquipment = async () => {
    try {
      const response = await makeAuthenticatedRequest(
        `/equipment/v2/character/${characterId}`,
        { method: 'GET' }
      );

      if (response.ok) {
        const data = await response.json();
        setEquipment(data);
      }
    } catch (err) {
      console.error('Error loading equipment:', err);
      setError('Failed to load equipment');
      if (err instanceof Error && err.message.includes('401')) {
        handleAuthError();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadInventory = async (char: any) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/crafting/crafted-items`,
        { method: 'GET' }
      );

      if (response.ok) {
        const data = await response.json();

        // Don't filter if we don't have character data
        if (!char) {
          console.error('❌ Cannot filter inventory: character data not loaded');
          setInventory([]);
          return;
        }

        console.log(`\n🔍 Filtering inventory for ${char.name} (${char.class}, Lv.${char.level})\n`);

        // Filter for unequipped items that this character can use
        const unequippedItems = data.filter((item: InventoryItem) => {
          if (item.isEquipped) {
            console.log(`Filtered out ${item.itemName}: already equipped`);
            return false;
          }

          // Check level requirement
          if (item.requiredLevel > char.level) {
            console.log(`❌ Filtered out ${item.itemName}: requires level ${item.requiredLevel}, character is ${char.level}`);
            return false;
          }

          // Check class requirement (if specified) - case insensitive
          // Important: Only filter if item has a class requirement
          if (item.requiredClass && item.requiredClass !== null && item.requiredClass.trim() !== '') {
            if (item.requiredClass.toLowerCase() !== char.class.toLowerCase()) {
              console.log(`❌ Filtered out ${item.itemName}: requires ${item.requiredClass}, character is ${char.class}`);
              return false;
            }
          }

          console.log(`✅ Keeping ${item.itemName} (Class: ${item.requiredClass || 'Any'}, Level: ${item.requiredLevel})`);
          return true;
        });

        console.log(`\n📦 Final filtered inventory for ${char.name} (${char.class}, Lv.${char.level}): ${unequippedItems.length} items\n`);

        // Separate consumables (itemType 3) from equipment
        const consumableItems = unequippedItems.filter((item: InventoryItem) => item.itemType === 3);
        const equipmentItems = unequippedItems.filter((item: InventoryItem) => item.itemType !== 3);

        // Sort alphabetically by item name
        const sortedItems = equipmentItems.sort((a: InventoryItem, b: InventoryItem) =>
          a.itemName.localeCompare(b.itemName)
        );
        const sortedConsumables = consumableItems.sort((a: InventoryItem, b: InventoryItem) =>
          a.itemName.localeCompare(b.itemName)
        );

        setInventory(sortedItems);
        setConsumables(sortedConsumables);
      }
    } catch (err) {
      console.error('Error loading inventory:', err);
      if (err instanceof Error && err.message.includes('401')) {
        handleAuthError();
      }
    }
  };

  const equipItem = async (itemId: number, slot: string) => {
    try {
      const slotEnum = getSlotEnumValue(slot);
      const response = await makeAuthenticatedRequest(
        `/equipment/v2/equip`,
        {
          method: 'POST',
          body: JSON.stringify({
            characterId: characterId,
            itemId: itemId,
            equipmentSlot: slotEnum
          })
        }
      );

      if (response.ok) {
        await loadEquipment();
        await loadInventory(character);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to equip item');
      }
    } catch (err) {
      console.error('Error equipping item:', err);
      setError('Failed to equip item');
      if (err instanceof Error && err.message.includes('401')) {
        handleAuthError();
      }
    }
  };

  const unequipItem = async (slot: string) => {
    try {
      const slotEnum = getSlotEnumValue(slot);
      const response = await makeAuthenticatedRequest(
        `/equipment/v2/unequip`,
        {
          method: 'POST',
          body: JSON.stringify({
            characterId: characterId,
            equipmentSlot: slotEnum
          })
        }
      );

      if (response.ok) {
        await loadEquipment();
        await loadInventory(character);
      }
    } catch (err) {
      console.error('Error unequipping item:', err);
      if (err instanceof Error && err.message.includes('401')) {
        handleAuthError();
      }
    }
  };

  const unequipAll = async () => {
    if (!window.confirm('Unequip all items from this character?')) {
      return;
    }

    setError('');
    const newSlots = ['mainHand', 'offHand', 'head', 'chest', 'legs', 'feet', 'hands', 'necklace', 'ring1', 'ring2', 'belt', 'back'];
    const legacySlots = ['weapon', 'armor', 'accessory'];

    try {
      // Unequip all new V2 slots
      await Promise.all(
        newSlots.map(async (slot) => {
          const slotEnum = getSlotEnumValue(slot);
          try {
            await makeAuthenticatedRequest(
              `/equipment/v2/unequip`,
              {
                method: 'POST',
                body: JSON.stringify({
                  characterId: characterId,
                  equipmentSlot: slotEnum
                })
              }
            );
          } catch (err) {
            // Ignore errors for empty slots
            console.log(`Slot ${slot} already empty or error:`, err);
          }
        })
      );

      // Unequip all legacy V1 slots
      await Promise.all(
        legacySlots.map(async (slotType) => {
          try {
            await makeAuthenticatedRequest(
              `/equipment/unequip`,
              {
                method: 'POST',
                body: JSON.stringify({
                  characterId: characterId,
                  slotType: slotType
                })
              }
            );
          } catch (err) {
            // Ignore errors for empty slots
            console.error(`Error unequipping legacy slot ${slotType}:`, err);
          }
        })
      );

      // Reload equipment and inventory
      await loadEquipment();
      await loadInventory(character);
    } catch (err) {
      console.error('Error unequipping all items:', err);
      setError('Failed to unequip all items');
      if (err instanceof Error && err.message.includes('401')) {
        handleAuthError();
      }
    }
  };

  const getSlotEnumValue = (slot: string): number => {
    const slotMap: { [key: string]: number } = {
      mainHand: 0,
      offHand: 1,
      head: 2,
      chest: 3,
      legs: 4,
      feet: 5,
      hands: 6,
      necklace: 7,
      ring1: 8,
      ring2: 9,
      belt: 10,
      back: 11
    };
    return slotMap[slot];
  };

  // Drag and Drop Handlers
  const handleDragStart = (item: InventoryItem) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, slot: string) => {
    e.preventDefault();
    if (!draggedItem) return;

    // Validate that the item can go in this slot
    if (!canEquipInSlot(draggedItem, slot)) {
      const slotName = draggedItem.equipmentSlot !== undefined
        ? `slot ${draggedItem.equipmentSlot}`
        : 'unknown slot';
      setError(`Cannot equip ${draggedItem.itemName} (${slotName}) in ${slot} slot`);
      setDraggedItem(null);
      return;
    }

    equipItem(draggedItem.id, slot);
    setDraggedItem(null);
  };

  // Helper function to check if an item can be equipped in a specific slot
  const canEquipInSlot = (item: InventoryItem, targetSlot: string): boolean => {
    // If item has the new equipmentSlot field, use that
    if (item.equipmentSlot !== undefined && item.equipmentSlot !== null) {
      const slotMap: { [key: string]: number[] } = {
        'mainHand': [0],       // Only MainHand items
        'offHand': [1],        // Only OffHand items
        'head': [2],           // Only Head items
        'chest': [3],          // Only Chest items
        'legs': [4],           // Only Legs items
        'feet': [5],           // Only Feet items
        'hands': [6],          // Only Hands items
        'necklace': [7],       // Only Necklace items
        'ring1': [8, 9],       // Rings can go in either ring slot
        'ring2': [8, 9],       // Rings can go in either ring slot
        'belt': [10],          // Only Belt items
        'back': [11]           // Only Back items
      };

      const allowedSlots = slotMap[targetSlot];
      if (!allowedSlots) return false;
      return allowedSlots.includes(item.equipmentSlot);
    }

    // Fall back to legacy itemType for old items
    // ItemType enum: 0=Weapon, 1=Armor, 2=Accessory
    const legacySlotMap: { [key: string]: number[] } = {
      'mainHand': [0],       // Weapons
      'offHand': [0],        // Weapons
      'head': [1],           // Armor
      'chest': [1],          // Armor
      'legs': [1],           // Armor
      'feet': [1],           // Armor
      'hands': [1],          // Armor
      'necklace': [2],       // Accessory
      'ring1': [2],          // Accessory
      'ring2': [2],          // Accessory
      'belt': [2],           // Accessory
      'back': [2]            // Accessory
    };

    const allowedTypes = legacySlotMap[targetSlot];
    if (!allowedTypes) return false;
    return allowedTypes.includes(item.itemType);
  };

  const renderEquipmentSlot = (slotName: string, label: string, item: EquipmentItem | null) => {
    const isEmpty = !item;

    return (
      <div
        className={`equipment-slot ${isEmpty ? 'empty' : 'filled'}`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, slotName)}
        onClick={() => !isEmpty && unequipItem(slotName)}
        title={isEmpty ? `Empty ${label} slot` : `Click to unequip ${item.name}`}
      >
        <div className="slot-label">{label}</div>
        {isEmpty ? (
          <div className="empty-slot-icon">📦</div>
        ) : (
          <div className="equipped-item">
            <div className="item-name">{item.name}</div>
            {item.isTwoHanded && <div className="two-handed-badge">⚔️ 2H</div>}
            <div className="item-stats">
              {item.attackBonus > 0 && <span className="stat-bonus">+{item.attackBonus} ATK</span>}
              {item.defenseBonus > 0 && <span className="stat-bonus">+{item.defenseBonus} DEF</span>}
              {item.hpBonus > 0 && <span className="stat-bonus">+{item.hpBonus} HP</span>}
              {item.mpBonus > 0 && <span className="stat-bonus">+{item.mpBonus} MP</span>}
              {item.speedBonus > 0 && <span className="stat-bonus">+{item.speedBonus} SPD</span>}
            </div>
            {/* Weapon Stats */}
            {(item.minDamage || item.maxDamage) && (
              <div className="weapon-stats" style={{ fontSize: '0.85em', marginTop: '4px', color: '#FFB74D' }}>
                <div>⚔️ {item.minDamage || 0}-{item.maxDamage || 0} {item.damageType || 'Physical'}</div>
                {(item.minRange || item.maxRange) && (
                  <div>📏 Range: {item.minRange || 1}-{item.maxRange || 1}</div>
                )}
                {item.weaponAPCost && item.weaponAPCost > 1 && (
                  <div>⚡ {item.weaponAPCost} AP</div>
                )}
                {item.aoeShape && item.aoeShape !== 'Single' && (
                  <div style={{ color: '#FF5722' }}>💥 {item.aoeShape}</div>
                )}
                {item.usesAmmo && (
                  <div style={{ color: item.currentAmmo === 0 ? '#F44336' : '#FFC107' }}>
                    🎯 {item.currentAmmo || 0}/{item.maxAmmo || 0} ammo
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const consumeItem = async (itemId: number) => {
    try {
      setError('');
      setMessage('');

      const response = await makeAuthenticatedRequest(
        `/crafting/use-consumable`,
        {
          method: 'POST',
          body: JSON.stringify({
            itemId: itemId,
            characterId: characterId
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessage(data.message);
        // Reload to show updated ammo and removed consumable
        await loadEquipment();
        await loadInventory(character);
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to use item');
      }
    } catch (err) {
      console.error('Error using consumable:', err);
      setError('Failed to use consumable');
      if (err instanceof Error && err.message.includes('401')) {
        handleAuthError();
      }
    }
  };

  if (isLoading) {
    return <div className="equipment-loading">Loading equipment...</div>;
  }

  if (!equipment || !character) {
    return <div className="equipment-error">Failed to load character equipment</div>;
  }

  return (
    <div className="character-equipment-screen">
      <div className="equipment-header">
        <button onClick={onBack} className="back-button">
          ← Back to Characters
        </button>
        <h2>{character.name}'s Equipment</h2>
        <button onClick={unequipAll} className="unequip-all-button">
          Unequip All
        </button>
      </div>

      {error && <div className="error-message" style={{ color: '#f44336', padding: '10px', marginBottom: '10px', background: 'rgba(244, 67, 54, 0.1)', borderRadius: '4px' }}>{error}</div>}
      {message && <div className="success-message" style={{ color: '#4caf50', padding: '10px', marginBottom: '10px', background: 'rgba(76, 175, 80, 0.1)', borderRadius: '4px' }}>{message}</div>}

      <div className="equipment-container">
        {/* Paper Doll - Equipment Slots */}
        <div className="paper-doll">
          <h3>Equipment Slots</h3>
          <div className="equipment-grid">
            {/* Row 1: Head */}
            <div className="slot-spacer"></div>
            {renderEquipmentSlot('head', 'Head', equipment.head)}
            <div className="slot-spacer"></div>

            {/* Row 2: Necklace */}
            <div className="slot-spacer"></div>
            {renderEquipmentSlot('necklace', 'Necklace', equipment.necklace)}
            <div className="slot-spacer"></div>

            {/* Row 3: Main Hand, Chest, Off Hand */}
            {renderEquipmentSlot('mainHand', 'Main Hand', equipment.mainHand)}
            {renderEquipmentSlot('chest', 'Chest', equipment.chest)}
            {renderEquipmentSlot('offHand', 'Off Hand', equipment.offHand)}

            {/* Row 4: Empty, Hands, Empty */}
            <div className="slot-spacer"></div>
            {renderEquipmentSlot('hands', 'Hands', equipment.hands)}
            <div className="slot-spacer"></div>

            {/* Row 5: Ring 1, Belt, Ring 2 */}
            {renderEquipmentSlot('ring1', 'Ring 1', equipment.ring1)}
            {renderEquipmentSlot('belt', 'Belt', equipment.belt)}
            {renderEquipmentSlot('ring2', 'Ring 2', equipment.ring2)}

            {/* Row 6: Back, Legs, Empty */}
            {renderEquipmentSlot('back', 'Back', equipment.back)}
            {renderEquipmentSlot('legs', 'Legs', equipment.legs)}
            <div className="slot-spacer"></div>

            {/* Row 7: Empty, Feet, Empty */}
            <div className="slot-spacer"></div>
            {renderEquipmentSlot('feet', 'Feet', equipment.feet)}
            <div className="slot-spacer"></div>
          </div>
        </div>

        {/* Inventory Panel */}
        <div className="inventory-panel">
          <h3>Inventory ({inventory.length} items)</h3>
          <p className="inventory-hint">Drag items to equipment slots</p>

          {/* Slot Filter */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ color: 'white', marginRight: '10px' }}>Filter by slot:</label>
            <select
              value={slotFilter}
              onChange={(e) => setSlotFilter(e.target.value)}
              style={{
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555',
              }}
            >
              <option value="all">All Slots</option>
              <option value="MainHand">Main Hand</option>
              <option value="OffHand">Off Hand</option>
              <option value="Head">Head</option>
              <option value="Chest">Chest</option>
              <option value="Legs">Legs</option>
              <option value="Feet">Feet</option>
              <option value="Hands">Hands</option>
              <option value="Necklace">Necklace</option>
              <option value="Ring1">Ring 1</option>
              <option value="Ring2">Ring 2</option>
              <option value="Belt">Belt</option>
              <option value="Back">Back</option>
            </select>
          </div>

          <div className="inventory-grid">
            {inventory
              .filter(item => {
                if (slotFilter === 'all') return true;
                const itemSlotName = getSlotName(item.equipmentSlot);
                // Handle rings - both Ring1 and Ring2 should show when filtering by either
                if (slotFilter === 'Ring1' || slotFilter === 'Ring2') {
                  return itemSlotName === 'Ring1' || itemSlotName === 'Ring2';
                }
                return itemSlotName === slotFilter;
              })
              .map(item => (
              <div
                key={item.id}
                className="inventory-item"
                draggable
                onDragStart={() => handleDragStart(item)}
                style={{
                  padding: '8px',
                  margin: '4px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid #555',
                  borderRadius: '4px',
                  cursor: 'grab'
                }}
              >
                <div className="item-name" style={{ fontWeight: 'bold', marginBottom: '4px' }}>{item.itemName}</div>
                {item.requiredClass && (
                  <div style={{ fontSize: '0.85em', color: '#90CAF9', marginBottom: '4px' }}>
                    {item.requiredClass} Lv.{item.requiredLevel}
                  </div>
                )}
                {item.isTwoHanded && <div className="two-handed-badge">⚔️ 2H</div>}

                {/* Base Stats */}
                {(item.attackBonus > 0 || item.defenseBonus > 0 || item.hpBonus > 0 || item.mpBonus > 0 || item.speedBonus > 0) && (
                  <div className="item-stats" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                    {item.attackBonus > 0 && <span style={{ color: '#F44336' }}>⚔️ +{item.attackBonus}</span>}
                    {item.defenseBonus > 0 && <span style={{ color: '#2196F3' }}>🛡️ +{item.defenseBonus}</span>}
                    {item.hpBonus > 0 && <span style={{ color: '#4CAF50' }}>❤️ +{item.hpBonus}</span>}
                    {item.mpBonus > 0 && <span style={{ color: '#9C27B0' }}>💙 +{item.mpBonus}</span>}
                    {item.speedBonus > 0 && <span style={{ color: '#FF9800' }}>⚡ +{item.speedBonus}</span>}
                  </div>
                )}

                {/* Weapon Stats */}
                {(item.minDamage || item.maxDamage) && (
                  <div style={{ fontSize: '0.85em', marginTop: '6px', padding: '4px', backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: '3px' }}>
                    <div style={{ color: '#FFB74D', fontWeight: 'bold' }}>
                      ⚔️ {item.minDamage || 0}-{item.maxDamage || 0} {item.damageType || 'Physical'}
                    </div>
                    {(item.minRange || item.maxRange) && (
                      <div style={{ color: '#81C784' }}>📏 Range: {item.minRange || 1}-{item.maxRange || 1}</div>
                    )}
                    {item.weaponAPCost && item.weaponAPCost > 1 && (
                      <div style={{ color: '#64B5F6' }}>⚡ {item.weaponAPCost} AP</div>
                    )}
                    {item.aoeShape && item.aoeShape !== 'Single' && (
                      <div style={{ color: '#FF5722' }}>💥 {item.aoeShape}</div>
                    )}
                    {item.usesAmmo && (
                      <div style={{ color: item.currentAmmo === 0 ? '#F44336' : '#FFC107' }}>
                        🎯 {item.currentAmmo || 0}/{item.maxAmmo || 0} ammo
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {inventory.filter(item => {
              if (slotFilter === 'all') return true;
              const itemSlotName = getSlotName(item.equipmentSlot);
              if (slotFilter === 'Ring1' || slotFilter === 'Ring2') {
                return itemSlotName === 'Ring1' || itemSlotName === 'Ring2';
              }
              return itemSlotName === slotFilter;
            }).length === 0 && (
              <div className="no-inventory">
                {slotFilter === 'all'
                  ? 'No equipment items in inventory'
                  : `No ${slotFilter} items in inventory`}
              </div>
            )}
          </div>

          {/* Consumables Section */}
          {consumables.length > 0 && (
            <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(76, 175, 80, 0.1)', borderRadius: '8px', border: '1px solid #4caf50' }}>
              <h4 style={{ color: '#4caf50', marginTop: 0 }}>🧪 Consumables ({consumables.length})</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {consumables.map(item => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '6px',
                      border: '1px solid #555'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: '#fff' }}>{item.itemName}</div>
                      {item.itemName.includes('Arrow') && (
                        <div style={{ fontSize: '0.85em', color: '#aaa', marginTop: '4px' }}>
                          Refills ammunition for ranged weapons
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => consumeItem(item.id)}
                      style={{
                        padding: '8px 16px',
                        background: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#4caf50'}
                    >
                      Use
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterEquipment;
